import { useContext } from 'react'
import { useAsync, useIsExtension } from '../utility'
import { buildMikasaClient } from '../../helpers/api'
import { snakeCaseKeys } from '../../helpers/format'
import { useFullListingData } from '../listing'
import { useAuthSettings } from '../authorization'
import { useZillowListingUrl } from '../browser'
import { AppContext } from '../../contexts'
import { PREQUAL_TOPIC_KEY } from '../../constants/directMessages'
import {
  EXTENSION_CLIENT_EVENT_SOURCE,
  EMBED_CLIENT_EVENT_SOURCE,
} from '../../constants/clientEvents'

const curry =
  f =>
  a =>
  (...args) =>
    f(a, ...args)

const sendMessageGroup = async (authSettings, metadata, questionsArray) => {
  const postBody = {
    message_group: {
      messages: questionsArray.map(message => snakeCaseKeys(message)),
      metadata,
    },
  }

  const client = await buildMikasaClient(authSettings)
  const endpoint = 'chromebot/message_groups'
  const response = client.post(endpoint, postBody)

  return response
}

const useCreateMetaData = () => {
  const { address, listingPrice } = useFullListingData() ?? {}
  const url = useZillowListingUrl()
  const isExtension = useIsExtension()
  const source = isExtension
    ? EXTENSION_CLIENT_EVENT_SOURCE
    : EMBED_CLIENT_EVENT_SOURCE
  const { marketTempSliderPrice, appreciationSliderPercent, includeRental } =
    useContext(AppContext)

  const metadata = {
    raw_address: address,
    listing_price: listingPrice,
    estimated_growth_rate: appreciationSliderPercent,
    proposed_offer: marketTempSliderPrice || listingPrice,
    considering_short_term_rental: includeRental,
    listing_url: url,
    source: source,
  }

  return metadata
}

export const useMessageGroupRequestAsync = () => {
  const metadata = useCreateMetaData()
  const authSettings = useAuthSettings()

  const messageFunc = curry(curry(sendMessageGroup)(authSettings))(metadata)
  return useAsync(messageFunc, { minimumDuration: true })
}

export const usePrequalRequestAsync = () => {
  const metadata = useCreateMetaData()
  const authSettings = useAuthSettings()

  const questionsArray = [
    {
      topic: 'a Listing they viewed',
      topicKey: PREQUAL_TOPIC_KEY,
      message: 'Can I get pre-qualified for this home?',
    },
  ]

  const messageFuncWithAuth = curry(sendMessageGroup)(authSettings)
  const messageFuncWithAuthAndMetadata = curry(messageFuncWithAuth)(metadata)
  const messageFuncWithQuestions = curry(messageFuncWithAuthAndMetadata)(
    questionsArray
  )

  return useAsync(messageFuncWithQuestions, { minimumDuration: true })
}
